import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Sequences vs. Series: Partial Sums Converging
// ─────────────────────────────────────────────────────────────────
function DiagramSequencesAndSeries() {
  const W = 900, H = 340;

  // Geometric series: aₙ = (1/2)ⁿ, sum = 1
  const r = 0.5;
  const terms = Array.from({ length: 12 }, (_, n) => Math.pow(r, n + 1));
  const partials = terms.reduce((acc, t, i) => {
    acc.push((acc[i - 1] || 0) + t);
    return acc;
  }, []);
  const limit = 1;

  // Left panel: bar chart of terms aₙ
  const lPanW = 420, lPanH = H - 20;
  const barOx = 50, barOy = 260, barScY = 200;
  const barW = 26, barGap = 8;

  // Right panel: partial sums converging to 1
  const rPanX = 450, rPanW = W - rPanX - 14;
  const sOx = rPanX + 50, sOy = 260, sSc = 22;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sequences, Series, and Partial Sums — Geometric Series aₙ = (½)ⁿ converging to 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── LEFT: term bar chart ── */}
        <rect x={10} y={10} width={lPanW} height={lPanH} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={10 + lPanW / 2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Individual Terms  aₙ = (½)ⁿ</text>
        <text x={10 + lPanW / 2} y={46} textAnchor="middle" fontSize="11" fill="#888">Each term is half the previous — the sequence → 0</text>

        {/* y-axis */}
        <line x1={barOx} y1={barOy + 8} x2={barOx} y2={barOy - barScY - 10} stroke="#1F3864" strokeWidth="1.4" />
        <line x1={barOx - 6} y1={barOy} x2={lPanW - 10} y2={barOy} stroke="#1F3864" strokeWidth="1.4" />
        {[0.25, 0.5, 0.75, 1.0].map(v => (
          <g key={v}>
            <line x1={barOx - 5} y1={barOy - v * barScY} x2={barOx + 5} y2={barOy - v * barScY} stroke="#bbb" strokeWidth="1" />
            <text x={barOx - 8} y={barOy - v * barScY + 4} textAnchor="end" fontSize="10" fill="#aaa">{v}</text>
            <line x1={barOx} y1={barOy - v * barScY} x2={lPanW - 14} y2={barOy - v * barScY} stroke="#f0f0f0" strokeWidth="1" />
          </g>
        ))}
        <text x={barOx - 8} y={barOy + 4} textAnchor="end" fontSize="10" fill="#aaa">0</text>

        {/* Bars */}
        {terms.map((t, i) => {
          const bx = barOx + 14 + i * (barW + barGap);
          const bh = t * barScY;
          const alpha = 1 - i * 0.06;
          return (
            <g key={i}>
              <rect x={bx} y={barOy - bh} width={barW} height={bh}
                fill="#2E75B6" opacity={Math.max(0.2, alpha)} rx={2} />
              <text x={bx + barW / 2} y={barOy + 14} textAnchor="middle" fontSize="10" fill="#888">{i + 1}</text>
              {i < 4 && (
                <text x={bx + barW / 2} y={barOy - bh - 5} textAnchor="middle" fontSize="9" fill="#2E75B6" fontWeight="700">
                  {t.toFixed(3)}
                </text>
              )}
            </g>
          );
        })}
        <text x={barOx + 14 + 12 * (barW + barGap) - 10} y={barOy + 14} fontSize="10" fill="#888">n</text>

        {/* Sequence → 0 annotation */}
        <line x1={barOx + 14} y1={barOy - terms[0] * barScY} x2={barOx + 14 + 11 * (barW + barGap)} y2={barOy - terms[11] * barScY}
          stroke="#c0392b" strokeWidth="1.5" strokeDasharray="6,3" />
        <text x={barOx + 14 + 6 * (barW + barGap)} y={barOy - 30} fontSize="11" fill="#c0392b" fontWeight="700">aₙ → 0  (necessary but not sufficient for convergence)</text>

        {/* ── RIGHT: partial sums ── */}
        <rect x={rPanX} y={10} width={rPanW} height={lPanH} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={rPanX + rPanW / 2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Partial Sums  Sₙ = Σᵢ₌₁ⁿ (½)ⁱ</text>
        <text x={rPanX + rPanW / 2} y={46} textAnchor="middle" fontSize="11" fill="#888">Sums accumulate and approach the limit S = 1</text>

        {/* axes */}
        <line x1={sOx} y1={sOy + 6} x2={sOx} y2={sOy - 230} stroke="#1F3864" strokeWidth="1.4" />
        <line x1={sOx - 6} y1={sOy} x2={sOx + 13 * sSc + 20} y2={sOy} stroke="#1F3864" strokeWidth="1.4" />

        {/* Limit line S=1 */}
        <line x1={sOx} y1={sOy - 200} x2={sOx + 13 * sSc + 16} y2={sOy - 200}
          stroke="#27ae60" strokeWidth="2" strokeDasharray="8,4" />
        <text x={sOx + 13 * sSc + 18} y={sOy - 196} fontSize="12" fill="#27ae60" fontWeight="700">S=1</text>

        {/* y ticks */}
        {[0.25, 0.5, 0.75, 1.0].map(v => (
          <g key={v}>
            <line x1={sOx - 4} y1={sOy - v * 200} x2={sOx + 4} y2={sOy - v * 200} stroke="#bbb" strokeWidth="1" />
            <text x={sOx - 8} y={sOy - v * 200 + 4} textAnchor="end" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Partial sum points connected */}
        {partials.map((s, i) => {
          const cx = sOx + (i + 1) * sSc, cy = sOy - s * 200;
          return (
            <g key={i}>
              {i > 0 && (
                <line x1={sOx + i * sSc} y1={sOy - partials[i - 1] * 200} x2={cx} y2={cy}
                  stroke="#2E75B6" strokeWidth="1.8" />
              )}
              <circle cx={cx} cy={cy} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5" />
              <text x={cx} y={sOy + 14} textAnchor="middle" fontSize="10" fill="#888">{i + 1}</text>
              {i < 4 && (
                <text x={cx + 6} y={cy - 6} fontSize="9" fill="#2E75B6">S₍{i + 1}₎={s.toFixed(3)}</text>
              )}
            </g>
          );
        })}

        {/* Convergence gap annotation */}
        {(() => {
          const n = 7, sn = partials[n], cx = sOx + (n + 1) * sSc;
          return (
            <g>
              <line x1={cx + 6} y1={sOy - sn * 200} x2={cx + 6} y2={sOy - 200}
                stroke="#e07b00" strokeWidth="2" />
              <line x1={cx + 2} y1={sOy - sn * 200} x2={cx + 10} y2={sOy - sn * 200} stroke="#e07b00" strokeWidth="1.5" />
              <line x1={cx + 2} y1={sOy - 200} x2={cx + 10} y2={sOy - 200} stroke="#e07b00" strokeWidth="1.5" />
              <text x={cx + 12} y={sOy - (sn + 1) * 100 + 4} fontSize="10" fill="#e07b00" fontWeight="700">error</text>
              <text x={cx + 12} y={sOy - (sn + 1) * 100 + 17} fontSize="10" fill="#e07b00">→ 0</text>
            </g>
          );
        })()}

        {/* Formula box bottom */}
        <rect x={rPanX + 8} y={H - 68} width={rPanW - 16} height={52} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1" />
        <text x={rPanX + rPanW / 2} y={H - 52} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Geometric Series Formula</text>
        <text x={rPanX + rPanW / 2} y={H - 35} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">S = a / (1 − r),  |r| &lt; 1</text>
        <text x={rPanX + rPanW / 2} y={H - 20} textAnchor="middle" fontSize="11" fill="#555">Here: S = (½)/(1−½) = 1  ✓</text>
      </svg>
      <div style={S.dCaption}>
        A sequence is an ordered list of numbers aₙ. A series is the sum Σaₙ — it converges if its partial sums Sₙ approach a finite limit. Here each term aₙ = (½)ⁿ is plotted as a bar (left) and the running totals Sₙ are plotted as dots climbing toward the limit S = 1 (right). The error between Sₙ and the true sum shrinks to zero.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Convergence Tests
// ─────────────────────────────────────────────────────────────────
function DiagramConvergenceTests() {
  const W = 900, H = 380;

  const tests = [
    {
      name: 'Divergence Test',
      color: '#c0392b', fill: '#fff0f0',
      condition: 'If lim aₙ ≠ 0  →  diverges',
      converse: 'lim aₙ = 0 does NOT mean it converges',
      when: 'Always try first — quickest way to prove divergence',
      example: 'Σ n/(n+1): lim = 1 ≠ 0  →  diverges',
      example2: 'Σ 1/n: lim = 0 BUT diverges (harmonic)',
    },
    {
      name: 'Geometric Series',
      color: '#2E75B6', fill: '#EBF3FB',
      condition: 'Σ arⁿ: converges iff |r| < 1\nSum = a/(1−r)',
      converse: 'Diverges if |r| ≥ 1',
      when: 'Any series with a fixed common ratio r',
      example: 'Σ (⅓)ⁿ: |r|=⅓<1  →  S = 1/(1−⅓) = 3/2',
      example2: 'Σ (−1)ⁿ: |r|=1  →  diverges',
    },
    {
      name: 'p-Series Test',
      color: '#27ae60', fill: '#f0fff4',
      condition: 'Σ 1/nᵖ: converges iff p > 1',
      converse: 'Diverges if p ≤ 1',
      when: 'Series of the form 1/nᵖ',
      example: 'Σ 1/n²: p=2>1  →  converges (=π²/6)',
      example2: 'Σ 1/n: p=1  →  diverges (harmonic)',
    },
    {
      name: 'Ratio Test',
      color: '#9b59b6', fill: '#f5d0ff',
      condition: 'L = lim |aₙ₊₁/aₙ|\nL<1: converges  L>1: diverges  L=1: inconclusive',
      converse: 'Best for factorials and exponentials',
      when: 'Series with n! or aⁿ in numerator/denominator',
      example: 'Σ n!/nⁿ: L = lim (n+1)!/(n+1)ⁿ⁺¹ · nⁿ/n!\n→ L = 1/e < 1  →  converges',
      example2: '',
    },
    {
      name: 'Integral Test',
      color: '#e07b00', fill: '#fff7ee',
      condition: 'If f is positive, decreasing, continuous\nΣf(n) and ∫f(x)dx converge or diverge together',
      converse: 'The integral value ≠ series sum',
      when: 'When f(x) is easy to integrate',
      example: 'Σ 1/n²: ∫1/x²dx = 1  →  both converge',
      example2: 'Gives error bounds via remainder integral',
    },
  ];

  const panW = (W - 20) / tests.length - 5;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Convergence Tests — The Five Essential Tests for Series</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {tests.map(({ name, color, fill, condition, converse, when, example, example2 }, i) => {
          const px = 10 + i * (panW + 6);
          return (
            <g key={name}>
              <rect x={px} y={10} width={panW} height={H - 20} rx={5} fill={fill} stroke={color} strokeWidth="1.8" />
              {/* Header */}
              <rect x={px + 4} y={14} width={panW - 8} height={28} rx={3} fill={color} />
              <text x={px + panW / 2} y={33} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{name}</text>

              {/* Condition box */}
              <rect x={px + 6} y={50} width={panW - 12} height={48} rx={3} fill="#fff" stroke={color} strokeWidth="1" />
              {condition.split('\n').map((ln, j) => (
                <text key={j} x={px + panW / 2} y={66 + j * 16} textAnchor="middle"
                  fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{ln}</text>
              ))}

              {/* When to use */}
              <text x={px + 8} y={114} fontSize="10" fill="#1F3864" fontWeight="700">When:</text>
              <text x={px + 8} y={128} fontSize="10" fill="#555" fontStyle="italic">{when}</text>

              {/* Caution */}
              <rect x={px + 6} y={140} width={panW - 12} height={24} rx={2} fill={color} fillOpacity="0.12" />
              <text x={px + 8} y={152} fontSize="9.5" fill={color} fontWeight="700">⚠ {converse}</text>

              {/* Examples */}
              <line x1={px + 8} y1={174} x2={px + panW - 8} y2={174} stroke="#ddd" strokeWidth="1" />
              <text x={px + 8} y={188} fontSize="10" fill="#1F3864" fontWeight="700">Example:</text>
              {example.split('\n').map((ln, j) => (
                <text key={j} x={px + 8} y={202 + j * 14} fontSize="9.5" fill="#333" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
              {example2 && (
                <text x={px + 8} y={202 + example.split('\n').length * 14 + 8}
                  fontSize="9.5" fill="#888" fontStyle="italic">{example2}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        No single test works for every series — choose based on the series' structure. Always try the Divergence Test first (free and fast). Use the Geometric and p-Series tests for their specific forms. The Ratio Test excels when factorials or exponentials appear. The Integral Test connects series to calculus and provides error bounds.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Absolute vs. Conditional Convergence & Alternating Series
// ─────────────────────────────────────────────────────────────────
function DiagramAlternatingSeries() {
  const W = 900, H = 340;

  // Alternating harmonic: Σ (−1)ⁿ⁺¹/n, converges to ln(2) ≈ 0.6931
  const altTerms = Array.from({ length: 14 }, (_, n) => Math.pow(-1, n) / (n + 1));
  const altPartials = altTerms.reduce((acc, t, i) => {
    acc.push((acc[i - 1] || 0) + t);
    return acc;
  }, []);
  const lnTwo = Math.log(2);

  // Absolute harmonic: Σ 1/n — diverges
  const absPartials = altTerms.reduce((acc, t, i) => {
    acc.push((acc[i - 1] || 0) + Math.abs(t));
    return acc;
  }, []);

  const ox = 470, oy = 255, scX = 30, scY = 160;
  const toX = n => ox + n * scX;
  const toY = y => oy - y * scY;

  const panX = 14, panW = 430;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Absolute vs. Conditional Convergence — Alternating Harmonic Series</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* ── LEFT: explanation panels ── */}
        <rect x={panX} y={10} width={panW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={panX + panW / 2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Three Levels of Convergence Behaviour</text>

        {[
          {
            y: 42, color: '#27ae60', fill: '#f0fff4', bc: '#27ae60',
            title: 'Absolutely Convergent',
            def: 'Σ|aₙ| converges  →  Σaₙ converges',
            note: 'The "safest" form. Rearranging terms\ndoes not change the sum.',
            ex: 'Σ (−1)ⁿ/n²:  Σ 1/n² converges (p=2)\n→ absolutely convergent',
          },
          {
            y: 142, color: '#e07b00', fill: '#fff7ee', bc: '#e07b00',
            title: 'Conditionally Convergent',
            def: 'Σaₙ converges BUT Σ|aₙ| diverges',
            note: 'Riemann Rearrangement: can be\nrearranged to sum to ANY value!',
            ex: 'Σ (−1)ⁿ⁺¹/n:  converges to ln 2\nBUT Σ 1/n diverges (harmonic)',
          },
          {
            y: 242, color: '#c0392b', fill: '#fff0f0', bc: '#c0392b',
            title: 'Divergent',
            def: 'Partial sums Sₙ do not approach any limit',
            note: 'Includes series that grow without\nbound or oscillate forever.',
            ex: 'Σ (−1)ⁿ: oscillates between 0 and 1\nΣ n: grows to infinity',
          },
        ].map(({ y, color, fill, bc, title, def, note, ex }) => (
          <g key={title}>
            <rect x={panX + 8} y={y} width={panW - 16} height={92} rx={4} fill={fill} stroke={bc} strokeWidth="1.3" />
            <text x={panX + 18} y={y + 16} fontSize="12" fill={color} fontWeight="700">{title}</text>
            <text x={panX + 18} y={y + 32} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">{def}</text>
            {note.split('\n').map((ln, j) => (
              <text key={j} x={panX + 18} y={y + 50 + j * 14} fontSize="10" fill="#555">{ln}</text>
            ))}
            {ex.split('\n').map((ln, j) => (
              <text key={j} x={panX + 18} y={y + 70 + j * 12} fontSize="10" fill={color} fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}

        {/* ── RIGHT: convergence plot ── */}
        <rect x={ox - 50} y={10} width={W - ox + 36} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={(ox - 50 + W - 14) / 2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Alternating Harmonic Series: Σ (−1)ⁿ⁺¹/n</text>

        {/* Axes */}
        <line x1={ox} y1={oy + 10} x2={ox} y2={toY(1.6)} stroke="#1F3864" strokeWidth="1.4" />
        <line x1={ox - 6} y1={oy} x2={toX(14) + 8} y2={oy} stroke="#1F3864" strokeWidth="1.4" />
        <text x={toX(14) + 10} y={oy + 4} fontSize="12" fill="#1F3864">n</text>
        <text x={ox + 4} y={toY(1.6) - 4} fontSize="12" fill="#1F3864">Sₙ</text>

        {/* y ticks */}
        {[0.25, 0.5, 0.75, 1.0, 1.25].map(v => (
          <g key={v}>
            <line x1={ox - 4} y1={toY(v)} x2={ox + 4} y2={toY(v)} stroke="#bbb" strokeWidth="1" />
            <text x={ox - 8} y={toY(v) + 4} textAnchor="end" fontSize="10" fill="#aaa">{v}</text>
            <line x1={ox} y1={toY(v)} x2={toX(14) + 4} y2={toY(v)} stroke="#f0f0f0" strokeWidth="1" />
          </g>
        ))}

        {/* Limit line ln(2) */}
        <line x1={ox} y1={toY(lnTwo)} x2={toX(14) + 4} y2={toY(lnTwo)}
          stroke="#27ae60" strokeWidth="2" strokeDasharray="8,4" />
        <text x={toX(14) + 6} y={toY(lnTwo) - 4} fontSize="11" fill="#27ae60" fontWeight="700">ln 2 ≈ 0.693</text>

        {/* Alternating partial sums — zigzag converging */}
        {altPartials.map((s, i) => {
          const cx = toX(i + 1), cy = toY(s);
          const prevCx = i > 0 ? toX(i) : null, prevCy = i > 0 ? toY(altPartials[i - 1]) : null;
          return (
            <g key={i}>
              {i > 0 && <line x1={prevCx} y1={prevCy} x2={cx} y2={cy} stroke="#2E75B6" strokeWidth="2" />}
              <circle cx={cx} cy={cy} r={4} fill={i % 2 === 0 ? '#c0392b' : '#2E75B6'} stroke="#fff" strokeWidth="1.5" />
              <text x={cx} y={oy + 16} textAnchor="middle" fontSize="10" fill="#888">{i + 1}</text>
            </g>
          );
        })}

        {/* Absolute partial sums — diverging */}
        {absPartials.map((s, i) => {
          const cx = toX(i + 1), cy = toY(s);
          const prevCx = i > 0 ? toX(i) : null, prevCy = i > 0 ? toY(absPartials[i - 1]) : null;
          if (cy < 14 || cy > H - 14) return null;
          return (
            <g key={i}>
              {i > 0 && prevCy > 14 && prevCy < H - 14 &&
                <line x1={prevCx} y1={prevCy} x2={cx} y2={cy} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3" />}
              <circle cx={cx} cy={cy} r={3} fill="#e07b00" stroke="#fff" strokeWidth="1" />
            </g>
          );
        })}

        {/* Legend */}
        <rect x={ox - 40} y={H - 72} width={W - ox + 22} height={56} rx={3} fill="#f8fbff" stroke="#ccd9ea" />
        <line x1={ox - 30} y1={H - 55} x2={ox - 10} y2={H - 55} stroke="#2E75B6" strokeWidth="2" />
        <circle cx={ox - 20} cy={H - 55} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5" />
        <text x={ox - 6} y={H - 51} fontSize="11" fill="#2E75B6" fontWeight="700">Alternating partial sums Sₙ  — converges (conditionally) to ln 2</text>
        <line x1={ox - 30} y1={H - 34} x2={ox - 10} y2={H - 34} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3" />
        <circle cx={ox - 20} cy={H - 34} r={3} fill="#e07b00" />
        <text x={ox - 6} y={H - 30} fontSize="11" fill="#e07b00" fontWeight="700">Absolute partial sums Σ 1/n  — diverges (harmonic series)</text>
      </svg>
      <div style={S.dCaption}>
        The alternating harmonic series Σ(−1)ⁿ⁺¹/n zigzags above and below ln 2, with each overshoot smaller than the last (blue/red dots). Meanwhile the sum of absolute values Σ1/n (orange dashed) grows without bound — proving the series is only conditionally convergent. The Alternating Series Test guarantees convergence when |aₙ| decreases monotonically to 0.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Power Series and Radius of Convergence
// ─────────────────────────────────────────────────────────────────
function DiagramPowerSeries() {
  const W = 900, H = 340;

  // Show partial sums of  Σ xⁿ = 1/(1-x)  for |x|<1
  // Plot the partial sums S₁, S₂, S₄, S₈ vs the true function 1/(1-x) on (-1, 0.95)
  const exact = x => 1 / (1 - x);
  const partialSum = (x, N) => Array.from({ length: N }, (_, n) => Math.pow(x, n)).reduce((a, b) => a + b, 0);

  const ox = 70, oy = 200, scX = 200, scY = 50;
  const toX = x => ox + (x + 1.1) * scX;
  const toY = y => oy - (y - 1) * scY;

  const makePath = (fn, x0, x1, step, yMin = -2, yMax = 8) => {
    const segs = [], cur = [];
    for (let x = x0; x <= x1; x += step) {
      const y = fn(x), sy = toY(y);
      if (!isFinite(y) || sy < 14 || sy > H - 14) {
        if (cur.length > 1) segs.push('M ' + cur.join(' L '));
        cur.length = 0;
      } else {
        cur.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
      }
    }
    if (cur.length > 1) segs.push('M ' + cur.join(' L '));
    return segs.join(' ');
  };

  const exactPath = makePath(exact, -1.08, 0.97, 0.01);
  const paths = [
    { N: 1, color: '#e07b00', label: 'S₁ = 1' },
    { N: 2, color: '#9b59b6', label: 'S₂ = 1+x' },
    { N: 4, color: '#c0392b', label: 'S₄' },
    { N: 8, color: '#27ae60', label: 'S₈' },
    { N: 16, color: '#2E75B6', label: 'S₁₆' },
  ].map(({ N, color, label }) => ({
    path: makePath(x => partialSum(x, N), -0.98, 0.97, 0.01),
    color, label, N,
  }));

  // Panel: radius of convergence info
  const panX = 590, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Power Series — Partial Sums of Σ xⁿ Converging to 1/(1−x) Inside |x| &lt; 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Graph panel */}
        <rect x={10} y={10} width={570} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={295} y={30} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Partial sums converge to 1/(1−x) inside |x|&lt;1 and diverge outside</text>

        {/* Convergence region shading */}
        <rect x={toX(-1)} y={14} width={toX(1) - toX(-1)} height={H - 28}
          fill="#d0e8ff" opacity="0.25" />
        <line x1={toX(-1)} y1={14} x2={toX(-1)} y2={H - 14} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="6,3" />
        <line x1={toX(1)} y1={14} x2={toX(1)} y2={H - 14} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="6,3" />
        <text x={toX(-1)} y={H - 16} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">x=−1</text>
        <text x={toX(1)} y={H - 16} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">x=1</text>
        <text x={(toX(-1) + toX(1)) / 2} y={H - 28} textAnchor="middle" fontSize="10" fill="#2E75B6">Interval of convergence: (−1, 1)</text>

        {/* Axes */}
        <line x1={toX(-1.08)} y1={oy} x2={toX(1.1)} y2={oy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={toY(8)} x2={ox} y2={toY(-1.5)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={toX(1.1) + 4} y={oy + 4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox + 4} y={toY(8) - 4} fontSize="12" fill="#1F3864">y</text>

        {/* x ticks */}
        {[-1, -0.5, 0, 0.5].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy - 4} x2={toX(v)} y2={oy + 4} stroke="#888" strokeWidth="1.2" />
            <text x={toX(v)} y={oy + 16} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* y ticks */}
        {[1, 2, 3, 4, 5].map(v => (
          <g key={v}>
            <line x1={ox - 4} y1={toY(v)} x2={ox + 4} y2={toY(v)} stroke="#888" strokeWidth="1.2" />
            <text x={ox - 8} y={toY(v) + 4} textAnchor="end" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Partial sum curves (thinner, behind) */}
        {paths.map(({ path, color, label, N }) => (
          <g key={N}>
            <path d={path} fill="none" stroke={color} strokeWidth={N === 16 ? 2 : 1.5}
              strokeDasharray={N < 8 ? '6,3' : N === 8 ? '8,2' : ''} opacity="0.85" />
          </g>
        ))}

        {/* Exact function (on top) */}
        <path d={exactPath} fill="none" stroke="#1F3864" strokeWidth="3" />
        <text x={toX(0.65)} y={toY(exact(0.65)) - 10} fontSize="13" fill="#1F3864" fontWeight="700">1/(1−x)</text>

        {/* Legend */}
        <rect x={20} y={20} width={160} height={118} rx={3} fill="#fff" stroke="#ccd9ea" />
        {[
          { color: '#1F3864', label: '1/(1−x)  exact', dash: '' },
          ...paths.map(({ color, label }) => ({ color, label, dash: '5,2' })),
        ].map(({ color, label, dash }, i) => (
          <g key={i}>
            <line x1={30} y1={34 + i * 18} x2={54} y2={34 + i * 18} stroke={color}
              strokeWidth={i === 0 ? 3 : 1.8} strokeDasharray={i === 0 ? '' : dash} />
            <text x={60} y={38 + i * 18} fontSize="11" fill={color} fontWeight={i === 0 ? '700' : '400'}
              fontFamily="'Courier New',monospace">{label}</text>
          </g>
        ))}

        {/* Right panel */}
        <rect x={panX} y={10} width={panW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={panX + panW / 2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Radius of Convergence</text>

        {[
          { y: 36, bg: '#EBF3FB', bc: '#2E75B6', lines: ['Power Series', 'Σ cₙ(x−a)ⁿ', 'centred at x = a'] },
          { y: 106, bg: '#fff7ee', bc: '#e07b00', lines: ['Radius R via Ratio Test', 'R = lim |cₙ/cₙ₊₁|', 'or R = 1/limsup|cₙ|^(1/n)'] },
          { y: 176, bg: '#f0fff4', bc: '#27ae60', lines: ['|x−a| < R  →  converges', '|x−a| > R  →  diverges', '|x−a| = R  →  check endpoints'] },
          { y: 246, bg: '#fff0f0', bc: '#c0392b', lines: ['Special cases', 'R = 0: only at x = a', 'R = ∞: all real x (e.g. eˣ)'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={panX + 8} y={y} width={panW - 16} height={64} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            {lines.map((ln, j) => (
              <text key={j} x={panX + 16} y={y + 17 + j * 17} fontSize="11"
                fill={j === 0 ? bc : j === 1 ? '#1F3864' : '#555'}
                fontWeight={j === 0 ? '700' : '400'}
                fontFamily={j === 1 ? "'Courier New',monospace" : "'Georgia',serif"}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A power series Σcₙ(x−a)ⁿ converges for x in an interval centred at a. Inside that interval (the shaded band) higher-order partial sums hug the true function ever more tightly. Outside the radius R the partial sums blow up. At the boundary endpoints convergence must be tested separately — the series may converge, diverge, or converge conditionally.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Taylor and Maclaurin Series
// ─────────────────────────────────────────────────────────────────
function DiagramTaylorSeries() {
  const W = 900, H = 350;

  // Plot eˣ and its Maclaurin partial sums
  const ex = x => Math.exp(x);
  const taylorEx = (x, N) =>
    Array.from({ length: N + 1 }, (_, n) => {
      let fact = 1; for (let k = 1; k <= n; k++) fact *= k;
      return Math.pow(x, n) / fact;
    }).reduce((a, b) => a + b, 0);

  const ox = 70, oy = 220, scX = 90, scY = 38;
  const toX = x => ox + (x + 1.5) * scX;
  const toY = y => oy - (y - 1) * scY;

  const makePath = (fn, x0, x1, step) => {
    const pts = [];
    for (let x = x0; x <= x1; x += step) {
      const sy = toY(fn(x));
      if (sy > 14 && sy < H - 14) pts.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
    }
    return pts.length > 1 ? 'M ' + pts.join(' L ') : '';
  };

  const approximations = [
    { N: 0, color: '#e07b00', label: 'T₀ = 1' },
    { N: 1, color: '#9b59b6', label: 'T₁ = 1+x' },
    { N: 2, color: '#c0392b', label: 'T₂ = 1+x+x²/2' },
    { N: 4, color: '#27ae60', label: 'T₄' },
    { N: 8, color: '#2E75B6', label: 'T₈' },
  ];

  // Right panel: table of common Maclaurin series
  const panX = 520, panW = W - panX - 14;

  const xTicks = [-1, 0, 1, 2, 3];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Taylor & Maclaurin Series — Polynomial Approximations to eˣ</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Graph panel */}
        <rect x={10} y={10} width={500} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={260} y={30} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Each higher-degree Taylor polynomial hugs eˣ over a wider range</text>

        {/* Approximation curves (behind) */}
        {approximations.map(({ N, color }) => (
          <path key={N} d={makePath(x => taylorEx(x, N), -1.4, 3.6, 0.03)}
            fill="none" stroke={color} strokeWidth="1.8" strokeDasharray={N < 4 ? '7,3' : ''} opacity="0.9" />
        ))}

        {/* Exact eˣ */}
        <path d={makePath(ex, -1.4, 3.6, 0.03)} fill="none" stroke="#1F3864" strokeWidth="3" />
        <text x={toX(2.4)} y={toY(ex(2.4)) - 10} fontSize="13" fill="#1F3864" fontWeight="700">eˣ</text>

        {/* Axes */}
        <line x1={toX(-1.5)} y1={oy} x2={toX(3.7)} y2={oy} stroke="#1F3864" strokeWidth="1.5" />
        <line x1={ox} y1={toY(9)} x2={ox} y2={toY(-0.5)} stroke="#1F3864" strokeWidth="1.5" />
        <text x={toX(3.7) + 4} y={oy + 4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox + 4} y={toY(9) - 4} fontSize="12" fill="#1F3864">y</text>

        {/* Ticks */}
        {xTicks.map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy - 4} x2={toX(v)} y2={oy + 4} stroke="#888" strokeWidth="1.2" />
            <text x={toX(v)} y={oy + 16} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}
        {[2, 4, 6, 8].map(v => (
          <g key={v}>
            <line x1={ox - 4} y1={toY(v)} x2={ox + 4} y2={toY(v)} stroke="#888" strokeWidth="1.2" />
            <text x={ox - 8} y={toY(v) + 4} textAnchor="end" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Legend */}
        <rect x={20} y={20} width={192} height={112} rx={3} fill="#fff" stroke="#ccd9ea" />
        {[{ color: '#1F3864', label: 'eˣ  (exact)', dash: '' }, ...approximations.map(({ color, label }) => ({ color, label, dash: '5,2' }))].map(({ color, label, dash }, i) => (
          <g key={i}>
            <line x1={30} y1={34 + i * 17} x2={54} y2={34 + i * 17} stroke={color}
              strokeWidth={i === 0 ? 3 : 1.8} strokeDasharray={i === 0 ? '' : dash} />
            <text x={60} y={38 + i * 17} fontSize="11" fill={color}
              fontWeight={i === 0 ? '700' : '400'}
              fontFamily="'Courier New',monospace">{label}</text>
          </g>
        ))}

        {/* Error annotation at x=2 for T2 */}
        {(() => {
          const xA = 2, yTrue = toY(ex(xA)), yApprox = toY(taylorEx(xA, 2));
          return (
            <g>
              <line x1={toX(xA) + 5} y1={yTrue} x2={toX(xA) + 5} y2={yApprox}
                stroke="#e07b00" strokeWidth="2" />
              <text x={toX(xA) + 8} y={(yTrue + yApprox) / 2 + 4} fontSize="10" fill="#e07b00" fontWeight="700">error</text>
            </g>
          );
        })()}

        {/* Right panel: key Maclaurin series */}
        <rect x={panX} y={10} width={panW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea" />
        <text x={panX + panW / 2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Common Maclaurin Series</text>
        <text x={panX + panW / 2} y={42} textAnchor="middle" fontSize="10" fill="#888">f(x) = Σ f⁽ⁿ⁾(a)/n! · (x−a)ⁿ</text>

        {[
          { fn: 'eˣ', series: 'Σ xⁿ/n!  =  1 + x + x²/2! + x³/3! + …', range: 'all x', color: '#2E75B6' },
          { fn: 'sin x', series: 'Σ (−1)ⁿx²ⁿ⁺¹/(2n+1)!', range: 'all x', color: '#c0392b' },
          { fn: 'cos x', series: 'Σ (−1)ⁿx²ⁿ/(2n)!', range: 'all x', color: '#c0392b' },
          { fn: 'ln(1+x)', series: 'Σ (−1)ⁿ⁺¹xⁿ/n  =  x − x²/2 + x³/3 − …', range: '−1 < x ≤ 1', color: '#27ae60' },
          { fn: '1/(1−x)', series: 'Σ xⁿ  =  1 + x + x² + x³ + …', range: '|x| < 1', color: '#27ae60' },
          { fn: '(1+x)ᵅ', series: 'Σ C(α,n) xⁿ  (Binomial series)', range: '|x| < 1', color: '#9b59b6' },
          { fn: 'arctan x', series: 'Σ (−1)ⁿ x²ⁿ⁺¹/(2n+1)', range: '|x| ≤ 1', color: '#e07b00' },
        ].map(({ fn, series, range, color }, i) => (
          <g key={fn}>
            <rect x={panX + 6} y={52 + i * 40} width={panW - 12} height={38} rx={3}
              fill={i % 2 === 0 ? '#f8fbff' : '#fff'} stroke="#eee" strokeWidth="0.5" />
            <rect x={panX + 6} y={52 + i * 40} width={4} height={38} rx={0} fill={color} />
            <text x={panX + 18} y={52 + i * 40 + 14} fontSize="12" fill={color}
              fontFamily="'Courier New',monospace" fontWeight="700">{fn}</text>
            <text x={panX + 18} y={52 + i * 40 + 28} fontSize="10" fill="#333"
              fontFamily="'Courier New',monospace">{series}</text>
            <text x={panX + panW - 10} y={52 + i * 40 + 14} textAnchor="end" fontSize="9"
              fill="#888">{range}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A Taylor series replaces a function with an infinite polynomial whose coefficients are determined by the function's derivatives at one point. Adding more terms extends the range over which the polynomial accurately tracks the function. The Maclaurin series (centred at 0) for eˣ, sin x, and cos x converge for all real x — making them universally useful in analysis, physics, and numerical computation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Special Series and Applications
// ─────────────────────────────────────────────────────────────────
function DiagramSpecialSeries() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Special Series, Harmonic Series, and Applications of Power Series</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Top header */}
        <rect x={14} y={10} width={W - 28} height={38} rx={5} fill="#1F3864" />
        <text x={W / 2} y={26} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">Famous Series — Closed-Form Sums and Their Applications</text>
        <text x={W / 2} y={42} textAnchor="middle" fontSize="11" fill="#aac8e8">Power series make it possible to integrate functions without antiderivatives, solve ODEs, and evaluate limits</text>

        {/* Famous series cards */}
        {[
          {
            x: 14, w: 196, color: '#2E75B6', fill: '#EBF3FB',
            title: 'Geometric Series',
            formula: 'Σ rⁿ = 1/(1−r)',
            cond: '|r| < 1',
            note: 'Foundation of all power series. Partial sum:\nSₙ = (1−rⁿ)/(1−r)',
            use: 'Finance: annuities, loan payments',
          },
          {
            x: 218, w: 196, color: '#c0392b', fill: '#fff0f0',
            title: 'Harmonic Series',
            formula: 'Σ 1/n  →  ∞',
            cond: 'diverges!',
            note: 'Grows without bound, but\nexceedingly slowly (log n pace).',
            use: 'Benchmark for divergence proofs',
          },
          {
            x: 422, w: 196, color: '#27ae60', fill: '#f0fff4',
            title: 'Basel Problem',
            formula: 'Σ 1/n² = π²/6',
            cond: 'p=2, converges',
            note: 'Proved by Euler 1734.\nClosed form involves π².',
            use: 'Probability, quantum physics',
          },
          {
            x: 626, w: 260, color: '#9b59b6', fill: '#f5d0ff',
            title: 'Leibniz Formula for π',
            formula: 'π/4 = 1 − 1/3 + 1/5 − 1/7 + …',
            cond: 'arctan(1) = π/4',
            note: 'From arctan series at x=1.\nBeautifully simple but converges slowly.',
            use: 'π computation (historical)',
          },
        ].map(({ x, w, color, fill, title, formula, cond, note, use }) => (
          <g key={title}>
            <rect x={x} y={58} width={w} height={112} rx={5} fill={fill} stroke={color} strokeWidth="1.5" />
            <rect x={x + 4} y={62} width={w - 8} height={22} rx={3} fill={color} />
            <text x={x + w / 2} y={77} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
            <rect x={x + 6} y={88} width={w - 12} height={28} rx={2} fill="#fff" stroke={color} strokeWidth="1" />
            <text x={x + w / 2} y={102} textAnchor="middle" fontSize="12" fill={color}
              fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>
            <text x={x + w / 2} y={113} textAnchor="middle" fontSize="9" fill="#888">({cond})</text>
            {note.split('\n').map((ln, j) => (
              <text key={j} x={x + 10} y={128 + j * 13} fontSize="10" fill="#444">{ln}</text>
            ))}
            <text x={x + 10} y={160} fontSize="9.5" fill={color} fontStyle="italic">Use: {use}</text>
          </g>
        ))}

        {/* Applications section */}
        <text x={W / 2} y={192} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Applications of Power Series</text>

        {[
          {
            x: 14, w: 210, color: '#2E75B6', fill: '#EBF3FB',
            title: 'Integrating Hard Functions',
            lines: ['∫ sin(x²) dx has no closed form', 'but sin(x²) = Σ(−1)ⁿx^(4n+2)/(2n+1)!', 'Integrate term by term:', '= Σ(−1)ⁿx^(4n+3)/[(4n+3)(2n+1)!]+C'],
          },
          {
            x: 232, w: 210, color: '#c0392b', fill: '#fff0f0',
            title: 'Evaluating Limits (L\'Hôpital Alt.)',
            lines: ['lim sin(x)/x  as x→0', 'sin x = x − x³/6 + x⁵/120 − …', 'sin(x)/x = 1 − x²/6 + x⁴/120 − …', '→ 1 as x → 0  ✓'],
          },
          {
            x: 450, w: 210, color: '#27ae60', fill: '#f0fff4',
            title: 'Solving Differential Equations',
            lines: ['y\' = y  →  try y = Σaₙxⁿ', 'Matching coefficients gives', 'aₙ = a₀/n!  →  y = a₀·eˣ', 'Power series recover known solutions'],
          },
          {
            x: 668, w: 218, color: '#e07b00', fill: '#fff7ee',
            title: 'Numerical Computation',
            lines: ['sin(0.1) ≈ 0.1 − (0.1)³/6 = 0.09983', 'Error < (0.1)⁵/120 < 10⁻⁸', 'Calculators/CPUs use truncated', 'Taylor series for trig, exp, ln'],
          },
        ].map(({ x, w, color, fill, title, lines }) => (
          <g key={title}>
            <rect x={x} y={200} width={w} height={128} rx={5} fill={fill} stroke={color} strokeWidth="1.5" />
            <rect x={x + 4} y={204} width={w - 8} height={22} rx={3} fill={color} />
            <text x={x + w / 2} y={219} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={x + 10} y={238 + j * 18} fontSize="10" fill="#333"
                fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The power of series goes far beyond computing sums. Term-by-term integration lets us antidifferentiate functions that have no closed-form antiderivative. Substituting the series for sin x into the limit sin(x)/x immediately gives 1 without L'Hôpital's rule. Power series solutions underpin quantum mechanics, signal processing, and every scientific computation library.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Series(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Series</h1>

      <p style={S.p}>
        A series is what you get when you add up the terms of a sequence — potentially infinitely many of them. The central question is whether that infinite sum settles at a finite value (converges) or grows without bound (diverges). Series are the language of approximation in mathematics: every calculator computing sin x or eˣ is really evaluating a truncated infinite series, and the theory of convergence tells us exactly how large the error is.
      </p>

      <InfoBox
        title="Core Idea: A Series is the Limit of Its Partial Sums"
        items={[
          "Series: Σ aₙ = a₁ + a₂ + a₃ + …  — an infinite sum of terms",
          "Partial sum: Sₙ = a₁ + a₂ + … + aₙ  — the sum of the first n terms",
          "Convergence: Σ aₙ = S  iff  lim Sₙ = S  (partial sums settle at a finite limit)",
          "Necessary condition: lim aₙ = 0 is required for convergence (but not sufficient)",
          "Power series: Σ cₙ(x−a)ⁿ — a series whose sum is a function of x",
        ]}
      />

      <h2 style={S.h2}>Sequences, Series, and Partial Sums</h2>
      <DiagramSequencesAndSeries />
      <Table2 rows={[
        ["Sequence",         "Ordered list a₁, a₂, a₃, … — each term has a definite position\nLimit: lim aₙ = L if terms get arbitrarily close to L"],
        ["Series",           "Σ aₙ = a₁ + a₂ + a₃ + …  — the sum of all terms\nConverges if partial sums approach a finite limit S"],
        ["Partial sum Sₙ",  "Sₙ = Σᵢ₌₁ⁿ aᵢ — sum of the first n terms only\nThe series converges iff lim Sₙ exists and is finite"],
        ["Geometric series", "Σ arⁿ⁻¹ = a/(1−r)  for |r| < 1\nPartial sum: Sₙ = a(1−rⁿ)/(1−r)"],
        ["Telescoping",      "Σ (bₙ − bₙ₊₁) = b₁ − lim bₙ\nMost terms cancel — partial sums collapse to two terms"],
        ["Index shift",      "Σₙ₌₀^∞ aₙ = Σₙ₌₁^∞ aₙ₋₁  — relabelling does not change the sum\nUseful for aligning indices in manipulations"],
      ]} />

      <h2 style={S.h2}>Convergence Tests</h2>
      <DiagramConvergenceTests />
      <Table2 rows={[
        ["Divergence test",   "If lim aₙ ≠ 0  →  Σaₙ diverges. Always try this first.\nIf lim aₙ = 0, the test is inconclusive — need another test"],
        ["Ratio test",        "L = lim |aₙ₊₁/aₙ|:  L<1 converges, L>1 diverges, L=1 inconclusive\nBest when aₙ contains n! or cⁿ"],
        ["Root test",         "L = lim |aₙ|^(1/n):  same conclusions as ratio test\nUseful when aₙ is a perfect nth power"],
        ["Comparison test",   "0 ≤ aₙ ≤ bₙ: if Σbₙ converges, so does Σaₙ\nIf Σaₙ diverges, so does Σbₙ"],
        ["Limit comparison",  "lim aₙ/bₙ = c > 0 (finite):  Σaₙ and Σbₙ same behaviour\nCleaner than direct comparison for rational-like terms"],
        ["Alternating series","Σ(−1)ⁿ bₙ converges if bₙ decreasing and bₙ → 0\nError bound: |S − Sₙ| ≤ bₙ₊₁  (next unused term)"],
        ["Integral test",     "Σf(n) converges ⟺ ∫f(x)dx converges (f positive, decr., cont.)\nAlso gives remainder estimate: Rₙ ≤ ∫ₙ^∞ f(x) dx"],
      ]} />

      <h2 style={S.h2}>Absolute and Conditional Convergence</h2>
      <DiagramAlternatingSeries />
      <Table2 rows={[
        ["Absolute convergence","Σ|aₙ| converges  →  Σaₙ converges absolutely\nAbsolutely convergent series can be rearranged freely"],
        ["Conditional convergence","Σaₙ converges but Σ|aₙ| diverges\nRiemann: can be rearranged to sum to any real number or ±∞"],
        ["Alternating series test","Σ(−1)ⁿbₙ converges if: (1) bₙ decreasing, (2) bₙ→0\nBoth conditions are necessary"],
        ["AST error bound",   "|S − Sₙ| ≤ bₙ₊₁ — error ≤ absolute value of first omitted term\nGives automatic error bound with no extra work"],
        ["Alternating harmonic","Σ(−1)ⁿ⁺¹/n = ln 2 ≈ 0.6931 — conditionally convergent\nRearranging can make it sum to 3/2, or π, or any value"],
        ["Absolute value test","If |Σaₙ| ≤ Σ|aₙ| (triangle inequality for series)\nShows absolute convergence is the stronger property"],
      ]} />

      <h2 style={S.h2}>Power Series and Radius of Convergence</h2>
      <DiagramPowerSeries />
      <Table2 rows={[
        ["Power series",      "Σ cₙ(x−a)ⁿ = c₀ + c₁(x−a) + c₂(x−a)² + …\nConverges in an interval centred at a (the centre)"],
        ["Radius R",          "R = lim |cₙ/cₙ₊₁|  (from ratio test on the series)\nAlso R = 1 / limsup |cₙ|^(1/n)  (Cauchy-Hadamard)"],
        ["Interval of conv.", "Open interval (a−R, a+R) for sure; check endpoints separately\nCan be [a−R,a+R], (a−R,a+R], [a−R,a+R), or (a−R,a+R)"],
        ["Differentiation",   "d/dx [Σcₙ(x−a)ⁿ] = Σ n·cₙ(x−a)ⁿ⁻¹, same R\nDifferentiate term-by-term inside the interval of convergence"],
        ["Integration",       "∫Σcₙ(x−a)ⁿ dx = Σcₙ(x−a)ⁿ⁺¹/(n+1) + C, same R\nIntegrate term-by-term — may gain convergence at endpoints"],
        ["Substitution",      "Replace x with a function: e.g. sin(x²) from sin series\nQuickest way to find power series of composite functions"],
      ]} />

      <h2 style={S.h2}>Taylor and Maclaurin Series</h2>
      <DiagramTaylorSeries />
      <Table2 rows={[
        ["Taylor series",     "f(x) = Σ f⁽ⁿ⁾(a)/n! · (x−a)ⁿ  centred at x = a\nRequires f to be infinitely differentiable at a"],
        ["Maclaurin series",  "Taylor series centred at a = 0\nf(x) = f(0) + f'(0)x + f''(0)x²/2! + …"],
        ["Taylor polynomial", "Tₙ(x) = Σₖ₌₀ⁿ f⁽ᵏ⁾(a)/k! · (x−a)ᵏ — finite truncation\nBest polynomial approximation of degree n near x = a"],
        ["Remainder Rₙ",      "f(x) = Tₙ(x) + Rₙ(x)\nLagrange form: Rₙ(x) = f⁽ⁿ⁺¹⁾(c)/(n+1)! · (x−a)ⁿ⁺¹  for some c"],
        ["Error bound",       "|Rₙ(x)| ≤ M/(n+1)! · |x−a|ⁿ⁺¹,  M = max|f⁽ⁿ⁺¹⁾| on interval\nUsed to determine how many terms are needed for accuracy"],
        ["Uniqueness",        "If a power series equals f on an interval, it must be the Taylor series\nCoefficients cₙ = f⁽ⁿ⁾(a)/n! are uniquely determined"],
      ]} />

      <h2 style={S.h2}>Special Series and Applications</h2>
      <DiagramSpecialSeries />
      <Table2 rows={[
        ["Euler's identity",  "eⁱᶿ = cos θ + i sin θ  — from Taylor series of eˣ with x = iθ\nAt θ=π: eⁱᵖ + 1 = 0  (links e, i, π, 1, 0)"],
        ["Basel problem",     "Σ 1/n² = π²/6 ≈ 1.6449  (Euler, 1734)\nΣ 1/n⁴ = π⁴/90;  all even p-series involve π"],
        ["Integrating series","∫₀¹ sin(x²)dx: substitute x→x² in sin series, integrate term-by-term\nGives series answer when no closed form exists"],
        ["ODE solutions",     "Assume y = Σaₙxⁿ, substitute into y'' + p(x)y' + q(x)y = 0\nMatch coefficients → recurrence relation for aₙ"],
        ["Fourier series",    "f(x) = a₀/2 + Σ[aₙcos(nx) + bₙsin(nx)]\nExpands periodic functions; used in signal processing"],
        ["Generating functions","F(x) = Σaₙxⁿ encodes the sequence {aₙ} as a power series\nProduct of generating functions corresponds to convolution"],
      ]} />

      <InfoBox
        title="Key Takeaway — Strategy for Series Problems"
        items={[
          "Is it geometric (arⁿ)? → Sum = a/(1−r) if |r|<1; diverges if |r|≥1.",
          "Is it a p-series (1/nᵖ)? → Converges iff p>1; harmonic (p=1) diverges.",
          "Does lim aₙ ≠ 0? → Diverges immediately by the Divergence Test.",
          "Contains n! or cⁿ? → Use the Ratio Test: L = lim |aₙ₊₁/aₙ|.",
          "Alternating signs and |aₙ| → 0 decreasing? → Converges by Alternating Series Test.",
          "Need to find where a power series converges? → Apply Ratio Test, solve for |x−a|<R, check endpoints.",
          "Need Taylor coefficients? → cₙ = f⁽ⁿ⁾(a)/n!; or substitute into a known Maclaurin series.",
          "Need an error bound? → |Rₙ| ≤ M·|x−a|ⁿ⁺¹/(n+1)! where M bounds the next derivative.",
        ]}
      />
    </div>
  );
}